<template>
  <v-dialog
    v-model="modalState"
    transition="dialog-bottom-transition"
    persistent
    width="1100"
  >
    <div class="bg-white poppins">
      <div class="modal-header py-4 align-center">
        <h4 class="mb-0 font-weight-bolder">{{ $t("order_update") }}</h4>
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </div>
      </div>
      <div class="modal-body d-flex flex-column scroll-y mx-5 mt-4">
        <v-row class="pa-0">
          <v-col cols="12" md="4" class="my-0 py-0">
            <v-autocomplete
              :label="$t('order_type')"
              v-model="formData.order_type_id"
              :items="serverData.order_types"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
            />
          </v-col>
          <v-col cols="12" md="4" class="my-0 py-0">
            <v-autocomplete
              :label="$t('carrier')"
              v-model="formData.carrierId"
              :items="serverData.carriers"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
            />
          </v-col>
          <v-col cols="12" md="4" class="my-0 py-0">
            <v-autocomplete
              :label="$t('carrier_account')"
              v-model="formData.carrier_account_id"
              :items="serverData.carrier_accounts"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
            />
          </v-col>
          <v-col cols="12" md="4" class="my-0 py-0">
            <v-autocomplete
              :label="$t('payment_type')"
              v-model="formData.paymenttypeId"
              :items="serverData.paymentTypes"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
            />
          </v-col>
          <v-col cols="12" md="4" class="my-0 py-0">
            <v-autocomplete
              :label="$t('shipment_payment_term')"
              v-model="formData.shipment_payment_term_id"
              :items="serverData.shipment_payment_terms"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
            />
          </v-col>
          <v-col cols="12" md="4" class="my-0 py-0">
            <v-autocomplete
              :label="$t('shipping_method')"
              v-model="formData.shipping_type_id"
              :items="serverData.shipping_types"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="my-0 py-0">
            <v-text-field
              :label="$t('delivery_number')"
              v-model="formData.deliveryNo"
              dense
              outlined
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" class="my-0 py-0">
            <v-text-field
              :label="$t('number_of_boxes')"
              v-model="formData.numberOfBoxes"
              dense
              outlined
              clearable
              type="number"
              @change="() => validateMinValue('staff_account_limit', 0)"
            />
          </v-col>
          <v-col cols="12" md="4" class="mt-0 mb-0 py-0 align-center">
            <v-autocomplete
              v-model="formData.rts_flag"
              :label="$t('rts')"
              item-text="text"
              item-value="index"
              :items="booleanYesNoValues"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="6" class="my-0 py-0">
            <v-text-field
              :label="$t('consignee_id')"
              v-model="formData.consignee_document_number"
              dense
              outlined
              clearable
            />
          </v-col>
          <v-col cols="12" md="6" class="my-0 py-0">
            <OrderSingleDatePicker
              :value="formData.consignee_birthday"
              label="Consignee birthday"
              :clearDate="
                () => {
                  formData.consignee_birthday = null;
                }
              "
              @on-date-submit="
                (date) => {
                  formData.consignee_birthday = date ?? null;
                }
              "
              class="poppins"
            />
          </v-col>
          <v-col cols="12" md="6" class="my-0 py-0">
            <v-autocomplete
              :label="$t('consignee_document_type')"
              v-model="formData.consignee_document_type_id"
              :items="serverData.document_types"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
            />
          </v-col>
          <v-col cols="12" md="6" class="my-0 py-0">
            <v-autocomplete
              :label="$t('consignee_document_country')"
              v-model="formData.consignee_document_country_id"
              :items="serverData.countries"
              item-text="text"
              item-value="index"
              dense
              outlined
              clearable
            />
          </v-col>
          <v-col cols="12" md="6" class="my-0 py-0">
            <OrderSingleDatePicker
              :value="formData.consignee_document_issue_date"
              label="Consignee document issue date"
              :clearDate="
                () => {
                  formData.consignee_document_issue_date = null;
                }
              "
              @on-date-submit="
                (date) => {
                  formData.consignee_document_issue_date = date ?? null;
                }
              "
              class="poppins"
            />
          </v-col>
          <v-col cols="12" md="6" class="my-0 py-0">
            <OrderSingleDatePicker
              :value="formData.consignee_document_expiry_date"
              label="Consignee document expiry date"
              :min="new Date().toLocaleDateString()"
              :clearDate="
                () => {
                  formData.consignee_document_expiry_date = null;
                }
              "
              @on-date-submit="
                (date) => {
                  formData.consignee_document_expiry_date = date ?? null;
                }
              "
              class="poppins"
            />
          </v-col>
          <v-col
            cols="col-12"
            md="4"
            class="my-0 py-0 pb-6"
            v-if="serverData?.consignee_document_url !== ''"
          >
            <v-btn
              elevation="0"
              @click="showConsigneeDetails(serverData.consignee_document_url)"
              >VIEW DOCUMENT DETAILS
            </v-btn>
          </v-col>

          <v-col cols="12" class="mt-0 mb-4 py-0">
            <v-textarea
              :label="$t('comment')"
              v-model="formData.comment"
              dense
              outlined
              clearable
            />
          </v-col>
        </v-row>
        <!--begin::Actions-->
        <div class="my-4 d-flex flex-grow-1 align-end justify-center">
          <button
            @click.prevent="clearFields"
            type="reset"
            class="btn btn-light me-3 px-5 py-3 ls1"
          >
            Clear
          </button>
          <button
            @click.prevent="submitStatusUpdate"
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
          >
            Submit
          </button>
        </div>
        <!--end::Actions-->
      </div>
    </div>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";
import Swal from "sweetalert2";
import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";
import OrderSingleDatePicker from "@/own/components/fulfillment/orders/OrderSingleDatePicker.vue";

export default {
  name: "Update3PLAWBModal",
  components: {
    OrderSingleDatePicker,
  },
  props: {
    refresher: { required: true, type: Function },
  },
  mixins: [fieldValueValidation],
  data: () => ({
    modalState: false,
    formData: {
      ordersId: null,
      order_type_id: null,
      carrierId: null,
      paymenttypeId: null,
      shipment_payment_term_id: null,
      shipping_type_id: null,
      deliveryNo: null,
      numberOfBoxes: null,
      rts_flag: null,
      carrier_account_id: null,
      consignee_document_number: null,
      consignee_document_type_id: null,
      consignee_document_country_id: null,
      consignee_document_expiry_date: null,
      consignee_document_issue_date: null,
      consignee_birthday: null,
      comment: null,
    },
    serverData: {},
  }),
  computed: {
    rts_flag_label() {
      return this.formData.rts_flag === 1 ? "Yes" : "No";
    },
    booleanYesNoValues: function () {
      return [
        { text: "No", index: 0 },
        { text: "Yes", index: 1 },
      ];
    },
  },
  methods: {
    showConsigneeDetails(url) {
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.download = "orders.csv";
        link.click();
      } else {
        return;
      }
    },
    toggleModal(data) {
      if (!this.modalState) {
        this.setDataFromServer(data);
      } else this.clearFields();
      this.modalState = !this.modalState;
    },
    setDataFromServer(data) {
      // console.log(data);
      this.serverData = {
        order_types: data.order_types,
        carriers: data.carriers,
        paymentTypes: data.paymentType,
        shipment_payment_terms: data.shipment_payment_terms,
        shipping_types: data.shipping_types,
        carrier_accounts: data.carrier_accounts,
        document_types: data.document_types,
        countries: data.countries,
        consignee_document_url: data.order?.consignee_document_url,
      };

      this.formData = { ...data.order };
      this.formData.ordersId = data.order.id;
    },
    submitStatusUpdate() {
      this.$store.commit(SET_PAGE_LOADING, true);

      ApiService.post(`/fulfillment/orders/update_3pl_awb_save`, {
        ...this.formData,
      })
        .then(() => {
          Swal.fire({
            title: "Updated",
            text: "Order is updated",
            icon: "success",
            showConfirmButton: false,
            timer: 2500,
          });
          this.refresher();
          this.toggleModal();
        })
        .finally(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });
    },
    clearFields() {
      this.formData = {
        ordersId: null,
        order_type_id: null,
        carrierId: null,
        paymenttypeId: null,
        shipment_payment_term_id: null,
        shipping_type_id: null,
        deliveryNo: null,
        numberOfBoxes: null,
        rts_flag: null,
        carrier_account_id: null,
        consignee_document_number: null,
        consignee_document_type_id: null,
        consignee_document_country_id: null,
        consignee_document_expiry_date: null,
        consignee_document_issue_date: null,
        consignee_birthday: null,
        comment: null,
      };
    },
  },
};
</script>
